/**
 * Authentication utilities for WKS Product Entry SPA
 * 
 * Handles JWT token management, validation, and session storage.
 */

export interface JWTPayload {
  iss: string;        // Issuer (WordPress site URL)
  iat: number;        // Issued at timestamp
  nbf: number;        // Not before timestamp
  exp: number;        // Expiration timestamp
  data: {
    user: {
      id: number;
      username: string;
      email: string;
      capabilities?: string[];
    }
  }
}

export interface AuthSession {
  token: string;
  user: {
    id: number;
    username: string;
    displayName: string;
  };
  expiresAt: number;
}

export interface TokenValidationResult {
  valid: boolean;
  expired?: boolean;
  malformed?: boolean;
  payload?: JWTPayload;
  error?: string;
}

/**
 * Decode a JWT token without verification (client-side only)
 * Note: Actual verification happens server-side
 */
export function decodeJWT(token: string): JWTPayload | null {
  if (!token || typeof token !== 'string') {
    return null;
  }

  const parts = token.split('.');
  if (parts.length !== 3) {
    return null;
  }

  try {
    // Decode the payload (second part)
    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded) as JWTPayload;
  } catch {
    return null;
  }
}

/**
 * Validate a JWT token structure and expiration
 * 
 * @param token - The JWT token string
 * @returns TokenValidationResult with validation status
 */
export function validateToken(token: string): TokenValidationResult {
  // Check for empty or non-string token
  if (!token || typeof token !== 'string') {
    return {
      valid: false,
      malformed: true,
      error: 'Token is missing or not a string'
    };
  }

  // Check token format (must have 3 parts separated by dots)
  const parts = token.split('.');
  if (parts.length !== 3) {
    return {
      valid: false,
      malformed: true,
      error: 'Token must have three parts separated by dots'
    };
  }

  // Check each part is non-empty
  if (parts.some(part => !part || part.length === 0)) {
    return {
      valid: false,
      malformed: true,
      error: 'Token parts cannot be empty'
    };
  }

  // Try to decode the payload
  let payload: JWTPayload;
  try {
    const decoded = atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'));
    payload = JSON.parse(decoded);
  } catch {
    return {
      valid: false,
      malformed: true,
      error: 'Token payload is not valid base64 or JSON'
    };
  }

  // Check required fields exist
  if (!payload.exp || typeof payload.exp !== 'number') {
    return {
      valid: false,
      malformed: true,
      error: 'Token missing expiration timestamp'
    };
  }

  // Check if token is expired
  const now = Math.floor(Date.now() / 1000);
  if (payload.exp < now) {
    return {
      valid: false,
      expired: true,
      payload,
      error: 'Token has expired'
    };
  }

  // Check nbf (not before) if present
  if (payload.nbf && payload.nbf > now) {
    return {
      valid: false,
      error: 'Token is not yet valid'
    };
  }

  return {
    valid: true,
    payload
  };
}

/**
 * Check if a user has a specific capability
 * 
 * Note: The standard JWT plugin doesn't include capabilities in the token.
 * If capabilities are not present, we return true and let the server-side
 * API endpoints enforce permissions (which they do via WordPress capability checks).
 * 
 * @param payload - The decoded JWT payload
 * @param capability - The capability to check (e.g., 'edit_products')
 * @returns boolean indicating if user has the capability (or true if not in token)
 */
export function hasCapability(payload: JWTPayload | null, capability: string): boolean {
  if (!payload) {
    return false;
  }
  
  // If capabilities are not included in the token (standard JWT plugin behavior),
  // assume the user has permission and let server-side enforce it
  if (!payload.data?.user?.capabilities) {
    return true;
  }
  
  return payload.data.user.capabilities.includes(capability);
}

/**
 * Check if user has edit_products capability
 * Returns true if capabilities not in token (server will enforce)
 */
export function canEditProducts(payload: JWTPayload | null): boolean {
  return hasCapability(payload, 'edit_products');
}

/**
 * Create Authorization header value from token
 */
export function createAuthHeader(token: string): string {
  return `Bearer ${token}`;
}

/**
 * Extract token from Authorization header
 */
export function extractTokenFromHeader(header: string): string | null {
  if (!header || typeof header !== 'string') {
    return null;
  }

  const match = header.match(/^Bearer\s+(.+)$/i);
  return match ? match[1] : null;
}

// Session storage keys
const SESSION_KEY = 'wks_auth_session';

/**
 * Store auth session in session storage
 */
export function storeSession(session: AuthSession): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  }
}

/**
 * Get auth session from session storage
 */
export function getSession(): AuthSession | null {
  if (typeof sessionStorage === 'undefined') {
    return null;
  }

  const stored = sessionStorage.getItem(SESSION_KEY);
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored) as AuthSession;
  } catch {
    return null;
  }
}

/**
 * Clear auth session from session storage
 */
export function clearSession(): void {
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.removeItem(SESSION_KEY);
  }
}

/**
 * Check if current session is valid (not expired)
 */
export function isSessionValid(): boolean {
  const session = getSession();
  if (!session) {
    return false;
  }

  const now = Math.floor(Date.now() / 1000);
  return session.expiresAt > now;
}
